import type { Column, Row } from "@tanstack/react-table";
import {
	flexRender,
	type ReactTable,
	type RowData,
	type TableFeatures,
} from "@tanstack/react-table";
import type React from "react";
import type { CommonFeatures } from "@/hooks/useTanstackTable";
import { IndeterminateCheckbox } from "@/components/IndeterminateCheckbox";
import { Loading } from "@/components/Loading/Loading";



interface DataTableProps<TFeatures extends TableFeatures, TData extends RowData> {
	readonly className?: string,
	readonly rowClassName?: (data: TData) => string,
	readonly onRowClick?: (e: React.MouseEvent, data: TData) => void,


	readonly table: ReactTable<TFeatures, TData>;
	readonly noResultsMessage: string;
	readonly errorMessage: string;
	readonly isLoading: boolean;
	readonly isError: boolean;
}
export function DataTable<TFeatures extends CommonFeatures, TData extends RowData>(props: DataTableProps<TFeatures, TData>) {
	const { noResultsMessage, errorMessage, isLoading, isError } = props;
	const table = props.table as unknown as ReactTable<CommonFeatures, TData>;
	const isSuccess = !isLoading && !isError;
	const columnCount = table.getVisibleLeafColumns().length;
	const isEmpty = isSuccess && table.getRowModel().rows.length === 0;

	return (
		<table className={props.className} style={{}}>
			<thead>
				{table.getHeaderGroups().map(headerGroup => {
					return (
						<tr key={headerGroup.id}>
							{headerGroup.headers.map(header => (
								<th key={header.id} colSpan={header.colSpan} className={`${header.column.columnDef.meta?.className ?? ""} ${getSortClass(header.column)}`} style={header.column.columnDef.meta?.style} onClick={header.column.getToggleSortingHandler()} >
									{flexRender(header.column.columnDef.header, header.getContext())}
								</th>
							))}
						</tr>
					);
				})}
			</thead>
			<tbody>
				{isError == true && (
					<tr>
						<td colSpan={columnCount} className="dt-center">
							{errorMessage}
						</td>
					</tr>
				)}
				{isLoading == true && <tr>
					<td colSpan={columnCount} className="dt-center">
						<Loading />
					</td>
				</tr>}
				{isEmpty == true && (
					<tr>
						<td colSpan={columnCount} className="dt-center">
							{noResultsMessage}
						</td>
					</tr>
				)}

				{(isError || isLoading) == false && table.getRowModel().rows.map(row => (
					<tr key={row.id} className={props.rowClassName == null ? "" : props.rowClassName(row.original)} onClick={event => { props.onRowClick?.(event, row.original); }}>
						{row.getVisibleCells().map(cell => (
							<td key={cell.id} className={cell.column.columnDef.meta?.className} style={cell.column.columnDef.meta?.style} >
								{flexRender(cell.column.columnDef.cell, cell.getContext())}
							</td>
						))}
					</tr>
				))}
			</tbody>
		</table>
	);
}


function getSortClass<TD extends RowData>(col: Column<CommonFeatures, TD>): string {
	if (col.getCanSort() === false) return "";
	const direction = col.getIsSorted();
	if (direction == false) return "sorting";
	return direction == "asc" ? "sorting sorting_asc" : "sorting sorting_desc";
};


export function getSelectionCell<TF extends CommonFeatures, TD extends RowData>(_row: Row<TF, TD>) {
	const row = _row as unknown as Row<CommonFeatures, TD>;
	return (
		<IndeterminateCheckbox
			className="checkboxRow"
			style={{ height: 20 }}
			checked={row.getIsSelected()}
			disabled={!row.getCanSelect()}
			// indeterminate={row.getIsSomeSelected()}
			onChange={() => { row.toggleSelected(); }}
		/>
	);
}

export interface Pagination {
	readonly pageIndex: number;
	readonly pageSize: number;
}